import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, CalendarCheck, MessageCircle, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import { SectionLabel, GoldButton, waLink, telLink } from "@/components/ui-kit";
import { business, faqs, services } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { name: "", phone: "", email: "", service: "", message: "", privacy: false };

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.15em] text-ink-800/60 font-semibold">{label}</span>
    <input {...props} className="mt-2 w-full bg-transparent border-b border-black/20 py-2.5 text-ink-800 focus:border-gold focus:outline-none transition-colors" />
  </label>
);

export default function Contatti() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) return toast.error("Accetta l'informativa sulla privacy per continuare.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(initial);
    } catch {
      toast.error("Si è verificato un errore. Riprova o scrivici su WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Contatti Body Evolution 2.0 | Palestra Lusciano" description="Contatta Body Evolution 2.0 a Lusciano. Prenota una prova gratuita, richiedi informazioni o scrivici su WhatsApp." />

      <section className="bg-ink text-ivory grain pt-36 pb-20 relative">
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
          <Reveal><SectionLabel dark>Contatti</SectionLabel></Reveal>
          <h1 className="mt-5 font-display font-medium tracking-tight text-4xl sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.1}>Contatta Body Evolution 2.0</MaskLine>
          </h1>
          <Reveal delay={0.25}><p className="mt-5 text-ivory/70 max-w-2xl leading-relaxed">Siamo a Lusciano, in Viale della Libertà 66. Scrivici, chiamaci o vieni a trovarci per scoprire il percorso più adatto a te.</p></Reveal>
          <Reveal delay={0.35}>
            <div className="mt-8 flex flex-wrap gap-4">
              <GoldButton href={telLink}><Phone size={17} /> Chiama ora</GoldButton>
              <a href={waLink()} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#25D366] text-white font-semibold text-sm"><MessageCircle size={17} /> Scrivici su WhatsApp</a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-14">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal><h2 className="font-display text-3xl text-ink-800">Richiedi informazioni</h2></Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="mt-8 space-y-7">
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Nome e cognome" data-testid="contact-name" value={form.name} onChange={set("name")} required />
                <Field label="Telefono" data-testid="contact-phone" value={form.phone} onChange={set("phone")} required />
              </div>
              <Field label="Email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} required />
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] text-ink-800/60 font-semibold">Servizio di interesse</span>
                <select data-testid="contact-service" value={form.service} onChange={set("service")} className="mt-2 w-full bg-transparent border-b border-black/20 py-2.5 text-ink-800 focus:border-gold focus:outline-none">
                  <option value="">Seleziona un servizio</option>
                  {services.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                  <option value="Prova gratuita">Prova gratuita</option>
                  <option value="Abbonamenti">Abbonamenti</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.15em] text-ink-800/60 font-semibold">Messaggio</span>
                <textarea data-testid="contact-message" value={form.message} onChange={set("message")} required rows={4} className="mt-2 w-full bg-transparent border-b border-black/20 py-2.5 text-ink-800 focus:border-gold focus:outline-none resize-none" />
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" data-testid="contact-privacy" checked={form.privacy} onChange={set("privacy")} className="mt-1 accent-gold w-4 h-4" />
                <span className="text-sm text-ink-800/60">Ho letto e accetto l'informativa sulla privacy e autorizzo il trattamento dei miei dati.</span>
              </label>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-ink-800 text-ivory font-semibold text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {loading ? <><Loader2 size={17} className="animate-spin" /> Invio...</> : <><CalendarCheck size={17} /> Invia richiesta</>}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-sand rounded-sm p-8 space-y-5">
                <div className="flex gap-4"><MapPin className="text-gold shrink-0" /><div><div className="font-semibold text-ink-800">Indirizzo</div><p className="text-ink-800/70 text-sm">{business.address}</p></div></div>
                <div className="flex gap-4"><Phone className="text-gold shrink-0" /><div><div className="font-semibold text-ink-800">Telefono / WhatsApp</div><a href={telLink} className="text-ink-800/70 text-sm hover:text-gold">{business.phone}</a></div></div>
                <div className="flex gap-4"><Mail className="text-gold shrink-0" /><div><div className="font-semibold text-ink-800">Email</div><a href={`mailto:${business.email}`} className="text-ink-800/70 text-sm hover:text-gold break-all">{business.email}</a></div></div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 rounded-sm overflow-hidden border border-black/10 h-72">
                <iframe title="Mappa Body Evolution 2.0" src={business.mapEmbed} data-testid="google-map" className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <Reveal><SectionLabel>Domande frequenti</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-display font-medium tracking-tight text-3xl lg:text-4xl text-ink-800">FAQ</h2></Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-black/10">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg text-ink-800 hover:text-gold hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-ink-800/70 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
